import type { ReactNode } from "react";
import type { Lead } from "../../types.ts";
import type { SelectLeadField, TextLeadField } from "./types.ts";

export function TextField({
  lead,
  field,
  label,
  icon,
  onCommit,
}: {
  lead: Lead;
  field: Exclude<TextLeadField, "notes">;
  label: string;
  icon?: ReactNode;
  onCommit: (changes: Partial<Lead>) => void;
}) {
  const value = String(lead[field] || "");
  return (
    <label className="detail-field">
      <span className="detail-label">{label}</span>
      <span className="field-ic-wrap">
        {icon}
        <input
          className={icon ? "field field-with-ic" : "field"}
          defaultValue={value}
          type="text"
          onBlur={(event) => commitText(event.currentTarget.value, value, field, onCommit)}
          onKeyDown={(event) => {
            if (event.key === "Enter") event.currentTarget.blur();
          }}
        />
      </span>
    </label>
  );
}

export function NumberField({
  lead,
  label,
  icon,
  onCommit,
}: {
  lead: Lead;
  label: string;
  icon?: ReactNode;
  onCommit: (changes: Partial<Lead>) => void;
}) {
  const value = lead.value ?? "";
  return (
    <label className="detail-field">
      <span className="detail-label">{label}</span>
      <span className="field-ic-wrap">
        {icon}
        <input
          className={icon ? "field field-with-ic" : "field"}
          defaultValue={value}
          type="number"
          onBlur={(event) => {
            const raw = event.currentTarget.value;
            const next = raw === "" ? 0 : Number(raw);
            if (!Number.isFinite(next) || next === (lead.value ?? 0)) return;
            onCommit({ value: next });
          }}
          onKeyDown={(event) => {
            if (event.key === "Enter") event.currentTarget.blur();
          }}
        />
      </span>
    </label>
  );
}

export function SelectField({
  lead,
  field,
  label,
  options,
  onCommit,
}: {
  lead: Lead;
  field: SelectLeadField;
  label: string;
  options: Array<{ value: string; label: string }>;
  onCommit: (changes: Partial<Lead>) => void;
}) {
  return (
    <label className="detail-field">
      <span className="detail-label">{label}</span>
      <select
        className="field"
        value={String(lead[field] || "")}
        onChange={(event) => onCommit({ [field]: event.currentTarget.value })}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>{option.label}</option>
        ))}
      </select>
    </label>
  );
}

export function TextareaField({
  lead,
  field,
  label,
  onCommit,
}: {
  lead: Lead;
  field: Extract<TextLeadField, "notes">;
  label: string;
  onCommit: (changes: Partial<Lead>) => void;
}) {
  const value = String(lead[field] || "");
  return (
    <label className="detail-field">
      <span className="detail-label">{label}</span>
      <textarea
        className="field"
        defaultValue={value}
        rows={4}
        onBlur={(event) => commitText(event.currentTarget.value, value, field, onCommit)}
      />
    </label>
  );
}

function commitText(
  nextValue: string,
  previousValue: string,
  field: TextLeadField,
  onCommit: (changes: Partial<Lead>) => void,
) {
  if (nextValue === previousValue) return;
  onCommit({ [field]: nextValue });
}
